// Helpers that keep Flatfile's column mapping honest for sheets whose
// destination fields are generated from data (e.g. one column per product).
//
// Flatfile's auto-mapper blends fuzzy matching, AI suggestions and mapping
// history learned from previous imports. For near-identical labels such as
// "6DGA-200(H) Typhoon" vs "6DGA-200(H.D) Typhoon" that can cross-map columns
// even though an exact label match exists. The rule enforced here is simple:
// an incoming column whose header exactly matches a destination field's label
// (or key) always belongs to that field.

const normalizeName = (name) => name ? String(name).trim().replace(/\s+/g, ' ').toLowerCase() : ''

// Index destination fields by normalized label and key. Names shared by more
// than one field are ambiguous and excluded, so we never guess between duplicates.
export const buildExactMatchIndex = (fields = []) => {
	const index = new Map()
	const ambiguous = new Set()
	for (const field of fields) {
		if (!field?.key) {
			continue
		}
		const names = new Set([normalizeName(field.label), normalizeName(field.key)])
		for (const name of names) {
			if (!name) {
				continue
			}
			const existing = index.get(name)
			if (existing && existing.key !== field.key) {
				ambiguous.add(name)
			} else {
				index.set(name, field)
			}
		}
	}
	for (const name of ambiguous) {
		index.delete(name)
	}
	return index
}

export const findExactField = (index, sourceField) =>
	index.get(normalizeName(sourceField?.label)) || index.get(normalizeName(sourceField?.key))

const buildExactEdge = (sourceField, field, preview) => ({
	sourceField,
	destinationField: field,
	...preview ? { preview } : {},
	metadata: {
		certainty: 'absolute',
		confidence: 1,
		source: 'exact'
	}
})

// Rewrite Flatfile's suggested mapping plan so that any source column whose
// header exactly matches a destination field wins that destination. Fuzzy
// suggestions are kept for columns without an exact match.
export const correctExecutionPlan = (plan, fields) => {
	const index = buildExactMatchIndex(fields)

	// Pass 1: mapped edges with an exact match claim their destination
	const claimedKeys = new Set()
	const desiredByEdge = new Map()
	for (const edge of plan.fieldMapping || []) {
		const exactField = findExactField(index, edge.sourceField)
		if (exactField && !claimedKeys.has(exactField.key)) {
			desiredByEdge.set(edge, exactField)
			claimedKeys.add(exactField.key)
		}
	}

	// Rescue unmapped source columns that have an unclaimed exact match
	const rescuedEdges = []
	const unmappedSourceFields = []
	for (const unmapped of plan.unmappedSourceFields || []) {
		const exactField = findExactField(index, unmapped.sourceField)
		if (exactField && !claimedKeys.has(exactField.key)) {
			claimedKeys.add(exactField.key)
			rescuedEdges.push(buildExactEdge(unmapped.sourceField, exactField))
		} else {
			unmappedSourceFields.push(unmapped)
		}
	}

	// Pass 2: rebuild the mapping around the exact-match claims
	let changed = rescuedEdges.length > 0
	const fieldMapping = [...rescuedEdges]
	for (const edge of plan.fieldMapping || []) {
		const desired = desiredByEdge.get(edge)
		if (desired) {
			if (edge.destinationField?.key === desired.key) {
				fieldMapping.push(edge)
			} else {
				changed = true
				fieldMapping.push(buildExactEdge(edge.sourceField, desired, edge.preview))
			}
		} else if (edge.destinationField && claimedKeys.has(edge.destinationField.key)) {
			// A fuzzy suggestion lost its destination to an exact match
			changed = true
			unmappedSourceFields.push({ sourceField: edge.sourceField })
		} else {
			fieldMapping.push(edge)
		}
	}

	const mappedKeys = new Set(fieldMapping.map((edge) => edge.destinationField?.key))
	const unmappedDestinationFields = (fields || [])
		.filter((field) => !mappedKeys.has(field.key))
		.map((field) => ({ destinationField: field }))

	return { changed, fieldMapping, unmappedSourceFields, unmappedDestinationFields }
}

// Given the mapping that was actually executed, work out which destination
// columns received data that exactly belongs to a different field. Each move
// says "the values currently in column `from` belong in column `to`".
export const buildColumnMoves = (executedMapping = [], fields) => {
	const index = buildExactMatchIndex(fields)
	const edges = executedMapping.filter((edge) => edge?.destinationField?.key)
	const claimedKeys = new Set()
	const moves = []

	// Columns that already landed on their exact match keep their claim
	for (const edge of edges) {
		const exactField = findExactField(index, edge.sourceField)
		if (exactField && exactField.key === edge.destinationField.key) {
			claimedKeys.add(exactField.key)
		}
	}
	for (const edge of edges) {
		const exactField = findExactField(index, edge.sourceField)
		const from = edge.destinationField.key
		if (!exactField || exactField.key === from || claimedKeys.has(exactField.key)) {
			continue
		}
		claimedKeys.add(exactField.key)
		moves.push({ from, to: exactField.key })
	}
	return moves
}

// Apply column moves to one record's values. Columns that only give data away
// are emptied; whatever a move displaces was data that lost its destination
// to an exact match and is dropped, mirroring correctExecutionPlan.
export const applyColumnMoves = (values = {}, moves = []) => {
	const next = {}
	for (const [key, cell] of Object.entries(values)) {
		next[key] = { value: cell?.value ?? null }
	}
	const targets = new Set(moves.map((move) => move.to))
	for (const move of moves) {
		if (!targets.has(move.from)) {
			next[move.from] = { value: null }
		}
	}
	for (const move of moves) {
		next[move.to] = { value: values[move.from]?.value ?? null }
	}
	return next
}

const RECORDS_PAGE_SIZE = 10000

export const fetchAllRecords = async (api, sheetId, options = {}) => {
	const records = []
	for (let pageNumber = 1; ; pageNumber++) {
		const response = await api.records.get(sheetId, { ...options, pageSize: RECORDS_PAGE_SIZE, pageNumber })
		const page = response?.data?.records || []
		records.push(...page)
		if (page.length < RECORDS_PAGE_SIZE) {
			return records
		}
	}
}

const RECORDS_UPDATE_CHUNK_SIZE = 1000

export const updateRecordsInChunks = async (api, sheetId, records) => {
	for (let start = 0; start < records.length; start += RECORDS_UPDATE_CHUNK_SIZE) {
		await api.records.update(sheetId, records.slice(start, start + RECORDS_UPDATE_CHUNK_SIZE))
	}
}
